import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useRef, useState, useTransition,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import { graphql, useRefetchableFragment } from 'react-relay';

import Address from 'src/components/Address';
import DashboardControl from 'src/components/Dashboard/DashboardControl';
import { WARNING_TIMEOUT, showTradeRuleMsg } from 'src/components/Dashboard/DashboardControl/helpers/common';
import LoadingOverlay from 'src/components/Dashboard/LoadingOverlay/index';
import { buildURLState, updateEntityShowState } from 'src/components/Dashboard/helpers/common';
import Loading from 'src/components/Loading';
import { APIConfig } from 'src/config';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';
import Map from 'src/enosikit/components/Map';
import FlashesStore from 'src/stores/FlashesStore';
import {
  MODAL_CLOSE_DELAY,
  SOURCE, SOURCE_TRADES, TIME_ZONE_SYSTEM,
  UNIT, UNIT_CURRENCY, UNIT_ENERGY, VIEW_SELECTOR,
  PROPERTY_SHOW_DEFAULT_VIEW,
} from 'src/util/constants';
import BrowserProtocol from 'src/util/history';
import { getDefaultLocale } from 'src/util/i18n/handler';
import logger from 'src/util/logger';
import {
  dateRangeToTimeRange, getDashboardTimeScope,
  timeInZoneAtStartOfDay, timespanChanged,
} from 'src/util/time';

import buildLocalizedDateRangeLabel from 'src/util/time/dateRange';
import PropertyShowTradeEnergy from './PropertyShowTradeEnergy';
import { buildMainData } from './data/mainDataBuilder';
import { chartViewSelector, downloadManager } from './helpers/propertyShow';

const PropertyShowFragment = graphql`
fragment PropertyShow on Query
@refetchable(queryName: "PropertyShowRefetchQuery")
@argumentDefinitions(
  id: {type: "ID!"},
  start: { type: "Timestamp!" },
  finish: { type: "Timestamp!" },
  timeZone: { type: "String!" }
  historianAggregation: { type: "String!" }
  tradeAggregation: { type: "String!"  }
) {
  property(id:$id) {
  id
  externalIdentifier
  active { start finish }
  title
  timezone
  publicHolidayRegion
  address {
    line1
    line2
    city
    state
    country
    postcode
    gps {
      latitude
      longitude
    }
  }
  propertyUsers(first: 500) {
    edges {
      node {
        id
        externalIdentifier
        active { start finish }
        user {
          id
          externalIdentifier
          email
          givenName
          familyName
          active { start finish }
        }
      }
    }
  }
  meters(first:500) {
    edges {
      node {
        id
        identifier
        externalIdentifier
        active { start finish }
        title
        tradePointId
        property {
          title
        }
        primaryBillingPoint {
          identifier
        }
        rules(first:999999999, start: $start, finish: $finish) {
          edges {
            node {
              id
              tradeType
              buyer {
                userId
                user {
                  id
                  email
                  givenName
                  familyName
                }
                tradePoint {
                  id
                  type
                  meter {
                    id
                    identifier
                    title
                    property {
                      id
                      title
                    }
                  }
                }
              }
              seller {
                userId
                user {
                  id
                  email
                  givenName
                  familyName
                }
                tradePoint {
                  id
                  type
                  meter {
                    id
                    identifier
                    title
                    property {
                      id
                      title
                    }
                  }
                }
              }
              state
              clauses {
                edges {
                  node {
                    price
                    ignorePublicHolidays
                    monthsOfYear
                    daysOfWeek
                    timesOfDay {
                      start { hours minutes }
                      finish { hours minutes }
                    }
                  }
                }
              }
              proposedAt
              proposedBy {
                id
                email
                givenName
                familyName
              }
              acceptedAt
              acceptedBy {
                id
                email
                givenName
                familyName
              }
              start
              finish
            }
          }
        }
        dataConsumed: calculatedData(start: $start, finish: $finish, aggregation: $historianAggregation, timeZone: $timeZone, metric: "elec_energy_consumed") {
          aggregation
          metric {
            identifier
          }
          timeZone
          timeRange {
            start
            finish
          }
          data {
            timestamp
            value
            flags {
              identifier
              description
            }
          }
        }
        dataGenerated: calculatedData(start: $start, finish: $finish, aggregation: $historianAggregation, timeZone: $timeZone, metric: "elec_energy_generated") {
          aggregation
          metric {
            identifier
          }
          timeZone
          timeRange {
            start
            finish
          }
          data {
            timestamp
            value
            flags {
              identifier
              description
            }
          }
        }
        tradeSetSummary(start: $start, finish: $finish, timeZone: $timeZone, aggregation: $tradeAggregation, groups: [SUMMARY_GROUP_TRADE_POINT, SUMMARY_GROUP_TRADE_RULE, SUMMARY_GROUP_DIRECTION, SUMMARY_GROUP_TRADE_TYPE]) {
          key {
            tradePointId
            ruleId
            direction
            type
          }
          data {
            range { start finish }
            buyerTradePointIds
            sellerTradePointIds
            directions
            types
            value
            volume
            averagePrice
            tradeCount
            intervalCount
          }
        }
      }
    }
  }
 }
}
`;

const PROGRESS_INITIALISED = 33;
const PROGRESS_PREPARING_DATA = 67;
const PROGRESS_FULLY_LOADED = 100;

/**
 * Description
 * @param {any} props
 * @returns {React.ReactComponentElement} - PropertyShow component
 */
function PropertyShow(props) {
  if (!props) {
    return <Loading />;
  }
  const {
    queryTimezone, match, queryAggregateBy, queryGroupBy, queryXAxisDomain, onDatespanStartChange,
  } = props;
  const [data, refetch] = useRefetchableFragment(PropertyShowFragment, props);
  const { property } = data || {};
  const { __fragmentOwner } = props || {};
  const { variables } = __fragmentOwner || {};
  const { start, finish } = variables || {};
  const propertyTimezone = property.timezone || APIConfig().DEFAULT_TIMEZONE || TIME_ZONE_SYSTEM;
  const timezone = queryTimezone || propertyTimezone;
  const { s, f, agg } = getDashboardTimeScope(start, finish, timezone);
  const initialView = {
    aggregateBy: queryAggregateBy || PROPERTY_SHOW_DEFAULT_VIEW.aggregateBy,
    groupBy: queryGroupBy || PROPERTY_SHOW_DEFAULT_VIEW.groupBy,
    xAxisDomain: queryXAxisDomain || PROPERTY_SHOW_DEFAULT_VIEW.xAxisDomain,
  };

  // I18n messages for loading overlay
  const intl = useIntl();
  const loadingData = intl.formatMessage({ id: 'property.property_show.loading_data.overlay.body.message', defaultMessage: 'Loading data...' });
  const preparingDataAndRendering = intl.formatMessage({ id: 'property.property_show.preparing_data_and_rendering.overlay.body.message', defaultMessage: 'Preparing data and rendering.' });
  const newDateRangeApplied = intl.formatMessage({ id: 'property.property_show.new_date_range_applied.overlay.body.message', defaultMessage: 'New date range applied.' });

  const [historianAggregation, setHistorianAggregation] = useState(agg);
  const [tradeAggregation, setTradeAggregation] = useState(agg);
  const [datespan, setDatespan] = useState({
    start: s,
    finish: f,
  });

  const [isPending, startTransition] = useTransition();
  const [unit, setUnit] = useState(UNIT_ENERGY);
  const [source, setSource] = useState(SOURCE_TRADES);
  const [tradeBtnClicks, setTradeBtnClicks] = useState(0);
  const [viewSelectorOpen, setViewSelectorOpen] = useState(false);
  const [downloadSelectorOpen, setDownloadSelectorOpen] = useState(false);
  const [chartView, setChartView] = useState(initialView);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [progressBarValue, setProgressBarValue] = useState(0);
  const [progressBarLabel, setProgressBarLabel] = useState('');
  const { aggregateBy, groupBy, xAxisDomain } = chartView;

  const firstTimeRender = useRef(true);

  const mainData = buildMainData(property, datespan);

  // Notify parent component whenever datespan.start changes
  useEffect(() => {
    if (datespan?.start && onDatespanStartChange) {
      onDatespanStartChange(datespan.start);
    }
  }, [datespan?.start, onDatespanStartChange]);

  const closeAndResetModal = () => {
    setProgressBarLabel('');
    setProgressBarValue(0);
    setIsModalOpen(false);
  };

  /**
   * Synchronizes current chart view state (date range, timezone, view settings) to browser URL
   * query parameters using REPLACE navigation to avoid creating new history entries.
   */
  const updateURLState = useCallback(() => {
    updateEntityShowState(datespan, match, chartView, timezone);
  }, [datespan, match, chartView, timezone]);

  const doRefetch = () => {
    const { start: startTime, finish: finishTime } = dateRangeToTimeRange(
      datespan,
      timezone,
    );
    const timespanDuration = finishTime.diff(startTime);

    let aggregate = 'PT30M';
    if (timespanDuration.as('days') > 7) {
      aggregate = 'P1D';
    }

    // Step 1: modal open → 33% + "Loading data..."
    setIsModalOpen(true);
    setProgressBarValue(PROGRESS_INITIALISED);
    setProgressBarLabel(loadingData);

    startTransition(() => {
      refetch(
        {
          start: startTime.toSeconds(),
          finish: finishTime.toSeconds(),
          timeZone: timezone,
          historianAggregation: aggregate,
          tradeAggregation: aggregate,
        },
        {
          onComplete: (error) => {
            if (error) {
              FlashesStore.flash(FlashesStore.ERROR, error);
              closeAndResetModal();
              return;
            }

            // Step 2: data loaded → 67% + "Preparing data and rendering"
            setProgressBarValue(PROGRESS_PREPARING_DATA);
            setProgressBarLabel(preparingDataAndRendering);

            // Step 3: ready to render → 100% + "Loading complete"
            requestAnimationFrame(() => {
              setProgressBarValue(PROGRESS_FULLY_LOADED);
              setProgressBarLabel(newDateRangeApplied);

              // Step 4: reset after short delay
              setTimeout(() => {
                closeAndResetModal();
              }, MODAL_CLOSE_DELAY);
            });

            const { location } = match;
            const { pathname } = location;
            setHistorianAggregation(aggregate);
            setTradeAggregation(aggregate);

            const includeViewParams = queryAggregateBy || queryGroupBy || queryXAxisDomain;
            const state = buildURLState(
              chartView,
              startTime,
              finishTime.minus({ days: 1 }),
              timezone,
              includeViewParams,
            );

            if (!state) {
              logger.warn('Failed to generate URL state, skipping update');
              return;
            }

            BrowserProtocol.navigate({
              action: 'REPLACE',
              pathname,
              search: `?${Object.keys(state).map((k) => [k, encodeURIComponent(state[k])].join('=')).join('&')}`,
              hash: '',
              state,
            });
          },
        },
      );
    });
  };

  useEffect(() => {
    if (!firstTimeRender.current) {
      doRefetch();
    }
  }, [datespan]);

  useEffect(() => {
    if (!firstTimeRender.current) {
      updateURLState();
    }
  }, [aggregateBy, groupBy, xAxisDomain]);

  useEffect(() => {
    firstTimeRender.current = false;

    const { location } = match;
    const { pathname } = location || { pathname: window.location.pathname };

    const includeViewParams = queryAggregateBy || queryGroupBy || queryXAxisDomain;

    const state = buildURLState(
      chartView,
      datespan.start,
      datespan.finish,
      timezone,
      includeViewParams,
    );

    if (!state) {
      logger.warn('Failed to generate URL state, skipping update');
      return;
    }

    const searchString = `?${Object.keys(state).map((k) => [k, encodeURIComponent(state[k])].join('=')).join('&')}`;

    if (window.location.search !== searchString) {
      BrowserProtocol.navigate({
        action: 'REPLACE',
        pathname,
        search: searchString,
        hash: '',
        state,
      });
    }
  }, []);

  const toggle = (dropdownType) => {
    if (dropdownType === VIEW_SELECTOR) {
      setViewSelectorOpen(!viewSelectorOpen);
    } else {
      setDownloadSelectorOpen(!downloadSelectorOpen);
    }
  };

  const controlSetStateFunc = (opts) => {
    const { group, value } = opts;

    let chartSource = source;
    let chartUnit = unit;
    let btnClicks = tradeBtnClicks;

    FlashesStore.reset();
    if (value === chartSource && chartSource === SOURCE_TRADES) {
      if (btnClicks === 0) {
        clearTimeout();
        btnClicks += 1;
      } else {
        showTradeRuleMsg(intl);
      }
      setTimeout(() => {
        setTradeBtnClicks(0);
      }, WARNING_TIMEOUT);
    }
    if (group === UNIT) {
      chartUnit = value;
    }
    if (group === SOURCE) {
      chartSource = value;
      if (chartUnit === UNIT_CURRENCY) {
        chartUnit = UNIT_ENERGY;
      }
    }
    setSource(chartSource);
    setUnit(chartUnit);
    setTradeBtnClicks(btnClicks);
  };

  const handleTimespanUpdate = (updatedStart, updatedFinish) => {
    const inputStart = timeInZoneAtStartOfDay(updatedStart, timezone);
    const inputFinish = timeInZoneAtStartOfDay(updatedFinish, timezone);
    const currentTimespan = {
      start: timeInZoneAtStartOfDay(datespan.start, timezone),
      finish: timeInZoneAtStartOfDay(datespan.finish, timezone),
    };
    const isTimespanChanged = timespanChanged(inputStart, inputFinish, currentTimespan);
    if (!isTimespanChanged) {
      return;
    }
    setDatespan({ start: updatedStart, finish: updatedFinish });
    setProgressBarLabel(loadingData);
  };

  const renderPropertyControls = (propertyShowData) => {
    const { start: startTime, finish: finishTime } = datespan;
    const locale = getDefaultLocale();
    const updatedStartTime = startTime.setLocale(locale).setZone(timezone);
    const updatedFinishTime = finishTime.setLocale(locale).setZone(timezone);

    return (
      <DashboardControl
        chartView={chartView}
        chartViewSelector={chartViewSelector}
        controlSetStateFunc={(opts) => controlSetStateFunc(opts)}
        downloadFunc={downloadManager}
        downloadSelectorOpen={downloadSelectorOpen}
        isModalOpen={isModalOpen}
        mainData={propertyShowData}
        timespan={{ start: updatedStartTime, finish: updatedFinishTime }}
        timespanUpdateFunc={handleTimespanUpdate}
        timezone={timezone}
        setChartViewFunc={setChartView}
        source={source}
        toggleFunc={toggle}
        viewSelectorOpen={viewSelectorOpen}
        unit={unit}
      />
    );
  };

  const {
    address, title,
  } = property;
  const addr = new Address(address);

  if (!mainData) {
    const pageError = intl.formatMessage({ id: 'property.property_show.page.error', defaultMessage: 'Data unable to be processed.' });
    FlashesStore.flash(FlashesStore.ERROR, pageError);
    return null;
  }

  const pageTitle = intl.formatMessage({ id: 'property.property_show.page.title', defaultMessage: 'Enosi - Property: {propertyTitle}' }, { propertyTitle: title });

  // Loading overlay props
  const loadingOverlayProps = {
    bodyLabel: intl.formatMessage({
      id: 'property.property_show.applying_new_date_range.overlay.body.message',
      defaultMessage: 'Updating to show {dateRange}.',
    }, {
      dateRange: buildLocalizedDateRangeLabel(
        intl,
        datespan.start,
        datespan.finish,
        timezone,
      ),
    }),
    headerLabel: (
      <FormattedMessage
        id="property.property_show.applying_new_date_range.overlay.header.message"
        defaultMessage="{calenderIcon} Applying new date range"
        values={{
          calenderIcon: <FontAwesomeIcon icon={faCalendar} />,
        }}
      />
    ),
    progressBarValue,
    progressLabel: progressBarLabel,
    updateProgress: setIsModalOpen,
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>
      {(isPending || isModalOpen) && <LoadingOverlay inputs={loadingOverlayProps} />}
      <Breadcrumbs breadcrumbs={[{
        name: <FormattedMessage id="property.property_show.breadcrumbs.properties.label" defaultMessage=" Properties" />, path: '/properties',
      }, { name: title }]}
      />
      <Heading
        controls={renderPropertyControls(mainData)}
        context={<FormattedMessage id="property.property_show.heading.overview.title" defaultMessage="Overview" />}
        subtitle={addr.simpleString()}
        title={title}
      />
      <PropertyShowTradeEnergy
        controlSetStateFunc={(opts) => controlSetStateFunc(opts)}
        chartView={chartView}
        handleTimespanUpdate={handleTimespanUpdate}
        mainData={mainData}
        historianAggregation={historianAggregation}
        source={source}
        tradeAggregation={tradeAggregation}
        timespan={dateRangeToTimeRange(
          datespan,
          timezone,
        )}
        timezone={timezone}
        unit={unit}
      />
      {address && <Map address={address} />}
    </>
  );
}

export default PropertyShow;

PropertyShow.propTypes = {
  match: PropTypes.shape({
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
  }).isRequired,
  relay: PropTypes.shape({
    refetch: PropTypes.func,
  }),
  queryTimezone: PropTypes.string,
  queryAggregateBy: PropTypes.string,
  queryGroupBy: PropTypes.string,
  queryXAxisDomain: PropTypes.string,
  initialView: PropTypes.shape({
    aggregateBy: PropTypes.string,
    groupBy: PropTypes.string,
    xAxisDomain: PropTypes.string,
  }).isRequired,
  onDatespanStartChange: PropTypes.func,
};

PropertyShow.defaultProps = {
  relay: undefined,
  queryTimezone: undefined,
  onDatespanStartChange: undefined,
  queryAggregateBy: undefined,
  queryGroupBy: undefined,
  queryXAxisDomain: undefined,
};
